import React from "react";
import {
  EntriesIcon,
  GameControllerIcon,
  LiveIcon,
  RacingIcon,
  SportsIcon,
} from "@picklebet/ui";
import {
  bettingTabSearch,
  isLiveTabSearch,
} from "@/features/betting/util/liveTab";
import { getTitlePriorityByLocale } from "@/features/betting/util/titleOrder";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import type { EventCounts } from "@/hooks/firestore/betting/useEventCounts";
import { getTotalCountForSports } from "@/utilities/helpers";
import { under } from "../utils/isNavItemActive";
import type { NavHub, NavItem, NavLocation } from "../types";
import { link } from "./shared";

const hubIcons: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  esports: GameControllerIcon,
  racing: RacingIcon,
  sports: SportsIcon,
};

const bettingPath = (hub: NavHub, titleSlug?: string) =>
  `/${[hub.pathPrefix, titleSlug, "betting"].filter(Boolean).join("/")}`;

// Match and race pages sit under the listing path. The Live tab belongs to
// the Live row instead.
const bettingRowActive = (path: string) => (location: NavLocation) =>
  under(path)(location) && !isLiveTabSearch(location.search);

// Every title counts, including those whose row is dropped for having no events.
const bettingHubActive = (hub: NavHub) => (location: NavLocation) =>
  !isLiveTabSearch(location.search) &&
  [
    bettingPath(hub),
    ...hub.titles.map(({ slug }) => bettingPath(hub, slug)),
  ].some((path) => under(path)(location));

// Opens a title on the first tab that has events. Racing has no tabs.
const landingTabSearch = (
  hub: NavHub,
  titleSlug: string,
  eventCounts: EventCounts | undefined,
) => {
  const has = (type: string) =>
    getTotalCountForSports(eventCounts, [titleSlug], [type]) > 0;

  if (hub.slug === "racing") return "";
  if (has("prematchMatches")) return bettingTabSearch("next");
  if (hub.slug === "sports" && has("liveMatches")) {
    return bettingTabSearch("live");
  }
  return bettingTabSearch("futures");
};

export const bettingHub = (
  hub: NavHub,
  eventCounts: EventCounts | undefined,
  locale: string | null,
  liveRow?: NavItem,
): NavItem => {
  const Icon = hubIcons[hub.slug];

  const titleRows = [...hub.titles]
    .sort(
      (a, b) =>
        getTitlePriorityByLocale(locale, a.slug) -
        getTitlePriorityByLocale(locale, b.slug),
    )
    .flatMap((title): NavItem | [] => {
      const count = getTotalCountForSports(
        eventCounts,
        [title.slug],
        ["matches", "outrights", "racing"],
      );
      if (!count) return [];

      const path = bettingPath(hub, title.slug);

      return {
        id: `${hub.slug}/${title.slug}`,
        text: title.name,
        icon: <TitleIcon title={title} />,
        target: link(
          `${path}${landingTabSearch(hub, title.slug, eventCounts)}`,
        ),
        badge: count,
        activeWhen: under(path),
      };
    });

  return {
    id: hub.slug,
    text: hub.name,
    icon: <Icon />,
    target: link(bettingPath(hub)),
    activeWhen: bettingHubActive(hub),
    children: [
      {
        id: `${hub.slug}/all`,
        text: `All ${hub.name}`,
        icon: <EntriesIcon />,
        target: link(bettingPath(hub)),
        activeWhen: bettingRowActive(bettingPath(hub)),
      },
      ...(liveRow ? [liveRow] : []),
      ...titleRows,
    ],
  };
};

export const live = (
  hub: NavHub,
  eventCounts: EventCounts | undefined,
  text = "Live",
): NavItem | undefined => {
  const count = getTotalCountForSports(
    eventCounts,
    hub.titles.map(({ slug }) => slug),
    ["liveMatches"],
  );
  if (!count) return undefined;

  return {
    id: `${hub.slug}/live`,
    text,
    icon: <LiveIcon />,
    target: link(`${bettingPath(hub)}${bettingTabSearch("live")}`),
    badge: count,
    activeWhen: (location) =>
      under(bettingPath(hub))(location) && isLiveTabSearch(location.search),
  };
};
